import { UserService } from '../../api/services/UserService';
import { CompanyWorkScheduleService } from '../../api/services/CompanyWorkScheduleService';
import type { Routemize_DTOs_CreateUserWorkingHourDTO } from '../../api/models/Routemize_DTOs_CreateUserWorkingHourDTO';
import type { Routemize_DTOs_UpdateUserWorkingHourDTO } from '../../api/models/Routemize_DTOs_UpdateUserWorkingHourDTO';
import type { Routemize_DTOs_UserWorkingHourDTO } from '../../api/models/Routemize_DTOs_UserWorkingHourDTO';
import { Routemize_Constants_WorkingHourStatus } from '../../api/models/Routemize_Constants_WorkingHourStatus';
import { System_DayOfWeek } from '../../api/models/System_DayOfWeek';

export { System_DayOfWeek };

// Monday-first display ordering
export const DAY_DISPLAY_ORDER: Record<string, number> = {
  [System_DayOfWeek.MONDAY]: 0,
  [System_DayOfWeek.TUESDAY]: 1,
  [System_DayOfWeek.WEDNESDAY]: 2,
  [System_DayOfWeek.THURSDAY]: 3,
  [System_DayOfWeek.FRIDAY]: 4,
  [System_DayOfWeek.SATURDAY]: 5,
  [System_DayOfWeek.SUNDAY]: 6,
};

export const DAY_LABELS: Record<string, string> = {
  [System_DayOfWeek.MONDAY]: 'Monday',
  [System_DayOfWeek.TUESDAY]: 'Tuesday',
  [System_DayOfWeek.WEDNESDAY]: 'Wednesday',
  [System_DayOfWeek.THURSDAY]: 'Thursday',
  [System_DayOfWeek.FRIDAY]: 'Friday',
  [System_DayOfWeek.SATURDAY]: 'Saturday',
  [System_DayOfWeek.SUNDAY]: 'Sunday',
};

export interface EffectiveScheduleDay {
  dayOfWeek: System_DayOfWeek;
  dayLabel: string;
  companyIsWorkDay: boolean;
  companyStartTime: string;
  companyEndTime: string;
  isOverridden: boolean;
  effectiveIsWorkDay: boolean;
  effectiveStartTime: string;
  effectiveEndTime: string;
  userOverrideIds: string[];
}

const ALL_DAYS: System_DayOfWeek[] = [
  System_DayOfWeek.MONDAY,
  System_DayOfWeek.TUESDAY,
  System_DayOfWeek.WEDNESDAY,
  System_DayOfWeek.THURSDAY,
  System_DayOfWeek.FRIDAY,
  System_DayOfWeek.SATURDAY,
  System_DayOfWeek.SUNDAY,
];

export interface RecurringDayHours {
  isWorkDay: boolean;
  startTime: string;
  endTime: string;
}

const normalizeWallClockTime = (s?: string | null): string | null => {
  if (!s) return null;
  const trimmed = s.trim();
  return trimmed.length >= 5 ? trimmed.slice(0, 5) : null;
};

/**
 * Derive effective work-day boundaries from recurring UserWorkingHour rows for
 * one weekday. Returns null when the user has no recurring override.
 */
export function resolveRecurringDayHours(
  recurringHours: Array<
    Pick<Routemize_DTOs_UserWorkingHourDTO, 'dayOfWeek' | 'startTime' | 'endTime' | 'status' | 'isEnabled'>
  >,
  dayOfWeek: System_DayOfWeek
): RecurringDayHours | null {
  const dayRecords = recurringHours.filter(
    h => h.dayOfWeek === dayOfWeek && h.isEnabled !== false
  );
  if (dayRecords.length === 0) return null;

  const available = dayRecords
    .filter(r => r.status === Routemize_Constants_WorkingHourStatus.AVAILABLE)
    .map(r => ({
      start: normalizeWallClockTime(r.startTime),
      end: normalizeWallClockTime(r.endTime),
    }))
    .filter((r): r is { start: string; end: string } => !!r.start && !!r.end)
    .sort((a, b) => a.start.localeCompare(b.start));

  if (available.length === 0) {
    return { isWorkDay: false, startTime: '09:00', endTime: '17:00' };
  }

  const workStart = available[0].start;
  const workEnd = [...available].map(w => w.end).sort().reverse()[0];
  return { isWorkDay: true, startTime: workStart, endTime: workEnd };
}

export class UserWorkScheduleManager {
  async fetchUserRecurringHours(userId: string): Promise<Routemize_DTOs_UserWorkingHourDTO[]> {
    const hours = await UserService.getApiAppUserGetUserWorkHourAysnc(userId);
    return hours ?? [];
  }

  async getEffectiveSchedule(userId: string): Promise<EffectiveScheduleDay[]> {
    const [companySchedule, userHours] = await Promise.all([
      CompanyWorkScheduleService.getApiAppCompanyWorkScheduleGetEnhancedWorkSchedule(),
      UserService.getApiAppUserGetUserWorkHourAysnc(userId),
    ]);

    const dailySchedules = companySchedule.dailySchedules ?? [];

    // Find fallback work hours from first weekday that is a work day
    const fallbackSchedule = dailySchedules.find(
      s => s.isWorkDay && s.startTime && [
        System_DayOfWeek.MONDAY, System_DayOfWeek.TUESDAY, System_DayOfWeek.WEDNESDAY,
        System_DayOfWeek.THURSDAY, System_DayOfWeek.FRIDAY,
      ].includes(s.dayOfWeek as System_DayOfWeek)
    );
    const fallbackStart = fallbackSchedule?.startTime ?? '09:00';
    const fallbackEnd = fallbackSchedule?.endTime ?? '17:00';

    return ALL_DAYS.map(dayOfWeek => {
      const companyDay = dailySchedules.find(s => s.dayOfWeek === dayOfWeek);
      const companyIsWorkDay = companyDay?.isWorkDay ?? true;
      const companyStartTime = companyDay?.startTime ?? fallbackStart;
      const companyEndTime = companyDay?.endTime ?? fallbackEnd;

      const userDayOverrides = userHours.filter(h => h.dayOfWeek === dayOfWeek && h.isEnabled !== false);
      const recurring = resolveRecurringDayHours(userHours, dayOfWeek);
      const isOverridden = recurring !== null;

      let effectiveIsWorkDay: boolean;
      let effectiveStartTime: string;
      let effectiveEndTime: string;

      if (recurring) {
        effectiveIsWorkDay = recurring.isWorkDay;
        effectiveStartTime = recurring.isWorkDay ? recurring.startTime : companyStartTime;
        effectiveEndTime = recurring.isWorkDay ? recurring.endTime : companyEndTime;
      } else {
        effectiveIsWorkDay = companyIsWorkDay;
        effectiveStartTime = companyStartTime;
        effectiveEndTime = companyEndTime;
      }

      return {
        dayOfWeek,
        dayLabel: DAY_LABELS[dayOfWeek],
        companyIsWorkDay,
        companyStartTime,
        companyEndTime,
        isOverridden,
        effectiveIsWorkDay,
        effectiveStartTime,
        effectiveEndTime,
        userOverrideIds: userDayOverrides.map(h => h.id!).filter(Boolean),
      };
    });
  }

  async enableDayOverride(
    userId: string,
    dayOfWeek: System_DayOfWeek,
    startTime: string,
    endTime: string,
    existingOverrideIds: string[]
  ): Promise<void> {
    // Delete any existing overrides for this day first
    for (const id of existingOverrideIds) {
      await UserService.deleteApiAppUserDeleteUserWorkHours(id);
    }

    const createDto: Routemize_DTOs_CreateUserWorkingHourDTO = {
      userId,
      dayOfWeek,
      startTime,
      endTime,
      status: Routemize_Constants_WorkingHourStatus.AVAILABLE,
      windowIndex: 0,
      windowName: 'Work Hours',
      isEnabled: true,
    };
    await UserService.postApiAppUserCreateUserWorkHourAysnc([createDto]);
  }

  async disableDayOverride(userId: string, existingOverrideIds: string[]): Promise<void> {
    for (const id of existingOverrideIds) {
      await UserService.deleteApiAppUserDeleteUserWorkHours(id);
    }
  }

  async updateDayHours(
    userId: string,
    dayOfWeek: System_DayOfWeek,
    startTime: string,
    endTime: string,
    existingOverrideIds: string[]
  ): Promise<void> {
    if (existingOverrideIds.length === 0) {
      // No existing override — create one
      await this.enableDayOverride(userId, dayOfWeek, startTime, endTime, []);
      return;
    }

    // Update the first override; delete the rest
    const [firstId, ...restIds] = existingOverrideIds;
    for (const id of restIds) {
      await UserService.deleteApiAppUserDeleteUserWorkHours(id);
    }

    const updateDto: Routemize_DTOs_UpdateUserWorkingHourDTO = {
      id: firstId,
      userId,
      dayOfWeek,
      startTime,
      endTime,
      status: Routemize_Constants_WorkingHourStatus.AVAILABLE,
      windowIndex: 0,
      windowName: 'Work Hours',
      isEnabled: true,
    };
    await UserService.putApiAppUserUpdateUserWorkHourAysnc([updateDto]);
  }
}

export const userWorkScheduleManager = new UserWorkScheduleManager();
